import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ComponentDefinition, ConnectionPoint, IdentifierType, SeriesSelectorViewModel,
    ObjectType, SelectorType, IValueViewModel, SelectionStrategy, AggregationMode, ActualDataType, AcquisitionStrategy
} from '../../models';
import { SearchEnable } from '../../controls';
import { IRootScope } from 'IRootScope';
import { IGroupSelectionService, ItagsImportService } from '../../services';
import { guidEmpty, importConnectionPointTypes, seriesAdded } from '../../common';
import { SeriesSelectionWidget } from './seriesSelection.widget';

class SeriesSelectionController implements angular.IComponentController {
    control: any;
    dahsSearchEnable = {
        name: true,
        unit: true,
        comment: true,
        group: false,
        variable: false,
        source: false,
        err: false,
        message: false,
        user: false,
        severity: false,
        app: false,
        tag: false,
        unused: true,
        ne: true
    };
    // Search in historian
    dahsSearchFormat = {
        unused: d => String(!d.isInUse),
        ne: d => String(!d.isAlreadyExisting)
    };
    displayQueries: boolean;
    extraViewModels: { [guid: string]: IValueViewModel };
    extraSelectorViewModels: { [guid: string]: SeriesSelectorViewModel };
    filteredSeries: SeriesSelectorViewModel[];
    isLoading: boolean;
    isSearchingDAHS: boolean;
    loadingMessage: string;
    displayedSeries: SeriesSelectorViewModel[];
    identifierType = IdentifierType;
    isBindable: boolean;
    isDisabled: boolean;
    isSearching: boolean;
    item: SeriesSelectionWidget;
    placeholder: string;
    pageLength = 15;
    pageNumber = 1;
    searchEnable: SearchEnable;
    searchString: string;
    selectedConnectionPoint: string; // guid of selected connection point
    selectedRowIndex: number;
    selectedSerie: SeriesSelectorViewModel;
    series: SeriesSelectorViewModel[];
    types = importConnectionPointTypes;

    readonly disablePlus: boolean;
    readonly excludeTags: boolean;
    readonly hasConnectionPoints: boolean;
    readonly initialSearchString: string;
    readonly showQueries: boolean = false;

    static $inject = [
        '$scope',
        '$rootScope',
        'groupSelectionService',
        'tagsImportService',
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private groupSelectionService: IGroupSelectionService,
        private _tagsImportService: ItagsImportService,
    ) {
    }

    $onInit() {
        this.series = this.series || [];
        this.filteredSeries = [];
        this.displayedSeries = [];
        this.selectedRowIndex = null;
        this.selectedSerie = null;
        this.selectedConnectionPoint = guidEmpty;

        if (this.initialSearchString) {
            this.searchString = this.initialSearchString;
        } else if (this.isBindable && !!this.groupSelectionService.selectedGroup()) {
            this.searchString = `g:${this.groupSelectionService.selectedGroup().name}`;
        }

        this.control = {
            more: this.more(),
            less: this.less(),
            flip: this.flip(),
            showQueries: this.showQueries
        };

        this.read();

        this._$scope.$watch(() => this.filteredSeries, () => {
            this.filter();
        });

        if (this.showQueries) {
            this.placeholder = 'Search for queries (q:query c:comment)';
            this.searchEnable = {
                tag: false,
                group: false,
                variable: false,
                unit: false,
                comment: true,
                identifier: false,
                source: false,
                query: true
            };
        } else {
            this.searchEnable = {
                tag: true,
                group: true,
                variable: true,
                unit: true,
                comment: true,
                identifier: true
            };
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['showQueries']?.currentValue) {
            this.displayQueries = this.showQueries;
        }
    }

    private read() {
        if (this.item && (!this.series || this.series.length === 0)) {
            this.isLoading = true;

            const promise = this.showQueries
                ? this.item.readQueries()
                : this.item.readData(this.excludeTags);

            promise.then(data => {
                this.series = data;
                this.filter();
                this.isLoading = false;
            });
        }
    }

    private filter() {
        if (this.filteredSeries.length > 0) {
            const n = Math.min(this.filteredSeries.length, this.pageNumber * this.pageLength);
            let filtered = _(this.filteredSeries);
            filtered = filtered.take(n);
            this.control.pageNumber = this.pageNumber;
            this.displayedSeries = filtered.value();
        } else {
            this.displayedSeries = [];
        }
    }

    more() {
        return () => {
            this.pageNumber++;
            this.filter();
        };
    }

    less() {
        return () => {
            if (this.pageNumber > 1) {
                this.pageNumber--;
                this.filter();
            }
        };
    }

    flip() {
        return () => {
            this.displayQueries = !this.displayQueries;
        };
    }

    setClickedRow(serie, $index: number) {
        this.selectedSerie = serie;
        this.selectedRowIndex = $index;
    }

    add(serie: SeriesSelectorViewModel): void {
        this._$rootScope.$broadcast(seriesAdded, serie);
    }

    getSerieGroupName(serie: SeriesSelectorViewModel): string {
        return (serie.groupUniqueName)
            ? serie.groupUniqueName
            : (serie.groupList)
                ? serie.groupList.map(g => g.uniqueName).join(', ')
                : '';
    }

    searchInHistorian() {
        if (this.selectedConnectionPoint && this.searchString) {
            this.extraViewModels = this.extraViewModels || {};
            this.isSearching = true;
            const lm = this.loadingMessage;
            this.loadingMessage = 'Searching for tags in historian';
            this._tagsImportService
                .search(this.selectedConnectionPoint, this.searchString)
                .then(result => {
                    const extrasSVM = {};
                    this.series = result
                        .map(d => {
                            const svm = {
                                comment: d.longComment,
                                groupList: [],
                                guid: d.guid,
                                name: d.name,
                                objectType: ObjectType.HistorianTag,
                                rawInformation: {
                                    tagName: d.name,
                                    tagDefinitionGuid: d.guid,
                                    tagPointId: d.pointId,
                                    connectionPointGuid: this.selectedConnectionPoint,
                                    digitalSet: d.digitalSet
                                },
                                type: SelectorType.historianTag,
                                unitName: d.unit
                            } as any;
                            extrasSVM[d.guid] = svm;
                            return svm;
                        });
                    this.extraSelectorViewModels = extrasSVM;
                    this.filteredSeries = this.series;
                    const evm: { [guid: string]: IValueViewModel } = (Object as any).assign({}, this.extraViewModels);
                    result.forEach(itd => {
                        evm[itd.guid] = {
                            acquisitionPointGuid: guidEmpty,
                            acquisitionStrategy: AcquisitionStrategy.Default,
                            actualDataType: ActualDataType.Float,
                            aggregationMode: AggregationMode.None,
                            canDelete: true,
                            creationDate: new Date().toISOString(),
                            creationUser: '',
                            dataSheet: '',
                            digitalSetGuid: '',
                            groupAbsolutePath: '',
                            groupList: [],
                            groupUniqueName: '',
                            guid: itd.guid,
                            hasReconciledValue: false,
                            identifierNames: [],
                            identifierType: IdentifierType.Tag,
                            isConstraint: false,
                            lastModificationDate: new Date().toISOString(),
                            lastModifier: '',
                            longComment: itd.longComment,
                            max: itd.maximum,
                            min: itd.minimum,
                            name: itd.name,
                            objectType: ObjectType.HistorianTag,
                            pfd: '',
                            pid: '',
                            precedentGroupList: [],
                            qualifierGuid: '',
                            qualifierName: '',
                            selectionStrategy: SelectionStrategy.Calculated,
                            shortComment: itd.shortComment,
                            shortName: itd.name,
                            tagGuid: itd.guid,
                            tagName: itd.name,
                            unitGuid: '',
                            unitName: itd.unit,
                            variableSubTypeGuid: '',
                            variableSubTypeName: '',
                            variableTypeGuid: '',
                            variableTypeName: ''
                        };
                    });
                    this.extraViewModels = evm;
                    this.filter();
                })
                .catch(() => {
                    // this._loggerService.logError('Failed to search.');
                })
                .finally(() => {
                    this.isSearching = false;
                    this.loadingMessage = lm;
                });
        }
    }

    changeConnectionPoint(connectionPoint: ConnectionPoint) {
        // don't mess with the condition it's complicated
        this.isSearchingDAHS = connectionPoint && connectionPoint.guid !== '00000000-0000-0000-0000-000000000000';
        this.series = [];
        this.filteredSeries = [];
        if (!connectionPoint || connectionPoint.guid === guidEmpty) {
            this.read();
        }
    }
}

export const wtSeriesSelection: ComponentDefinition = {
    componentId: 'wtSeriesSelection',
    controller: SeriesSelectionController,
    template: require('./seriesSelection.component.html'),
    controllerAs: 'vm',
    bindings: {
        actualBeginDate: '=?',
        actualEndDate: '=?',
        control: '=?',
        disablePlus: '<?',
        displayedSeries: '=?',
        excludeTags: '<?',
        extraSelectorViewModels: '=?',
        extraViewModels: '=?',
        filteredSeries: '=?',
        hasConnectionPoints: '<?',
        initialSearchString: '@?',
        isBindable: '=?',
        isDisabled: '<?',
        item: '=',
        selectedSerie: '=?',
        series: '=?',
        showQueries: '<?'
    }
};